import { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import {
  IgButton,
  IgChip,
  IgDivider,
  IgIconButton,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  ButtonBase,
  CircularProgress,
  Dialog,
  Stack,
  useTheme,
} from "@mui/material";
import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { useAgenticLogicRuleScenarios } from "@src/api/agentic-logic/get-agentic-logic-rule-scenarios";
import { ProductPicker } from "@src/components/MainApp/SharedProductPicker/ProductPicker";
import {
  ShopifyItemPicker,
  ShopifyItemPickerItem,
} from "@src/components/MainApp/ShopifyItemPicker/ShopifyItemPicker";
import { useShopifyPickerItems } from "@src/components/MainApp/ShopifyItemPicker/useShopifyPickerItems";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { useDispatch, useSelector } from "@src/store";
import {
  closeProductPicker,
  getProductPickerSelectedProducts,
  openProductPicker,
} from "@src/store/slices/productPicker";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";
import { useImmer } from "use-immer";

import { graphConditionsOnDelivery } from "../graph/conditionSubject";
import { useRuleSelectionGraph } from "../graph/useRuleSelectionGraph";
import { resolveAgenticLogicView } from "../surface/surface";
import { useActiveAgenticLogicSurface } from "../surface/useActiveAgenticLogicSurface";
import {
  DEFAULT_RULE_DETAIL_TAB,
  isRuleDetailTabKey,
  RULE_DETAIL_TAB_PARAM,
} from "../workspace/ruleDetailTabs";
import {
  CART_DELIVERY_MODIFIERS,
  CartDeliveryModifier,
  CartPickKind,
  formatCartPickReply,
  formatSuggestedCartRun,
  MAX_CART_PICK_PRODUCTS,
} from "./cartPickReply";

const ASK =
  "Time to test the rule — which cart should I run the steps against?";
const PRODUCT_PICKER_TYPE = "agenticLogicCartTest";

/**
 * The rows the card opens on: a cart built from whatever the merchant picks
 * out of the catalog. Copy is the Figma's.
 */
const GENERIC_ROWS: { kind: CartPickKind; label: string }[] = [
  { kind: "products", label: "Specific products" },
  { kind: "collection", label: "Products from a collection" },
  { kind: "tag", label: "Products with a tag" },
];

const CATALOG_LABEL = "Pick your own cart";
const BACK_LABEL = "Back to suggested carts";

const ITEM_PICKERS: Record<
  Exclude<CartPickKind, "products">,
  {
    title: string;
    searchPlaceholder: string;
    itemNoun: { singular: string; plural: string };
  }
> = {
  collection: {
    title: "Select a collection",
    searchPlaceholder: "Search collections",
    itemNoun: { singular: "collection", plural: "collections" },
  },
  tag: {
    title: "Select product tags",
    searchPlaceholder: "Search product tags",
    itemNoun: { singular: "tag", plural: "tags" },
  },
};

/**
 * The carts Atlas can run, offered above the composer.
 *
 * Testing a rule is a conversation, not a control on the canvas: picking a
 * cart here sends an ordinary user message, and Atlas answers it by running
 * the cart through its own tool. That keeps one path to a run — the graph has
 * no picker of its own — and lets the same request be typed instead ("try a
 * blue bikini XXL plus a mug"), which no chip row could express.
 *
 * Two kinds of cart are on offer. The rule's own example carts are the list:
 * generated for its branches so each one exercises a step, they are the carts
 * worth running first, and a merchant who has just watched the rule get built
 * has no cart of their own in mind yet. Behind a last row, "Pick your own
 * cart", are the three catalog rows — specific products, a collection, or a
 * tag — for the merchant who does. Those open a picker, and Done sends the
 * pick as ids for the agent to resolve — the frontend never assembles a cart
 * line, which needs a variant, a price and the product's tags and collections.
 * A rule whose example carts have not been generated has only the catalog
 * rows, shown outright with no way back to a list that does not exist. Only
 * one list is on screen at a time, so a way out is never a fourth row under
 * carts the merchant did not ask for.
 *
 * The card is rendered locally rather than parked by the agent, so it is
 * already there when the author lands on the tab: offering it costs no turn,
 * and nothing is preselected. The agent's own question always outranks it —
 * whenever one is parked, this steps aside so the composer shows a single ask.
 * The picker dialogs are rendered outside that guard: a stream starting while
 * a merchant is mid-pick must not close the dialog under them.
 *
 * Under either list sits a Delivery row of toggle chips, but only for a rule
 * that branches on the checkout delivery. Such a rule (pickup, a rate,
 * transit days) cannot be lit by a cart alone, so a chosen modifier rides
 * along on whichever cart is picked next as a clause the cart-run prompt
 * turns into the delivery block. A rule that never reads the delivery is not
 * asked about it: the row would be a question with no bearing on the run. A
 * modifier on its own is not a run: it changes nothing until a cart is chosen.
 */
export const CartScenarioAsk = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // This ask renders inside the rail, which lives outside the page tree, so
  // the surface comes from the registry. An embedded surface namespaces its
  // params away from the host's own query string.
  const surface = useActiveAgenticLogicSurface();
  const view = resolveAgenticLogicView(params, surface?.ruleParam);
  const requestedTab = params.get(surface?.tabParam ?? RULE_DETAIL_TAB_PARAM);
  const tab = isRuleDetailTabKey(requestedTab)
    ? requestedTab
    : DEFAULT_RULE_DETAIL_TAB;
  const ruleId = view.kind === "rule" ? view.ruleId : null;
  const isOnSelectionLogic = ruleId !== null && tab === "selection_logic";

  const { inputRequirement, submitInput, disabled, streaming } =
    useExecutionPanelContext();
  const scenariosQuery = useAgenticLogicRuleScenarios(ruleId ?? undefined);
  // The message names the rule, so the merchant reads a sentence about their
  // own rule rather than a uuid — and so a pick still means the rule they are
  // LOOKING at when the rail is still showing an earlier rule's conversation.
  const ruleQuery = useAgenticLogicRule(ruleId ?? undefined);
  const ruleName = ruleQuery.data?.rule.name ?? null;
  // Whether the rule reads the checkout delivery at all decides whether the
  // Delivery row is offered — the graph is what says so.
  const offersDelivery = graphConditionsOnDelivery(
    useRuleSelectionGraph(ruleId ?? undefined).graph
  );

  const productPickerOpen = useSelector(
    isProductPickerOpen(PRODUCT_PICKER_TYPE)
  );
  const selectedProducts = useSelector(getProductPickerSelectedProducts);
  // Which item picker (collection / tag) is open; the product picker's open
  // state is the store's, keyed by type so the Exclusions tab's cannot collide.
  const [itemPicker, setItemPicker] = useImmer<Exclude<
    CartPickKind,
    "products"
  > | null>(null);

  // Dismissal lasts as long as this visit: the key changes when the rule or
  // the tab does, so leaving Selection logic and coming back offers the carts
  // again without any timer or stored flag. A pick in progress is abandoned
  // with the visit — it was about the rule that is no longer on screen.
  const visitKey = `${ruleId ?? ""}:${tab}`;
  const [dismissedVisitKey, setDismissedVisitKey] = useImmer<string | null>(
    null
  );
  const [lastVisitKey, setLastVisitKey] = useImmer(visitKey);
  // Whether the merchant stepped past the rule's example carts to the catalog
  // rows behind "Pick your own cart". Reset with the visit, so the suggested
  // carts lead again next time — and they lead outright when the set arrives
  // for a rule that had none when the card first rendered.
  const [showingCatalog, setShowingCatalog] = useImmer(false);
  // The delivery pinned to the next cart run; a toggle, so a second click
  // clears it. Abandoned with the visit like a pick in progress, and ignored
  // once a regenerated graph stops reading the delivery — the row is gone,
  // so a pin made under the old graph must not ride along unseen.
  const [pinnedDelivery, setDelivery] = useImmer<CartDeliveryModifier | null>(
    null
  );
  const delivery = offersDelivery ? pinnedDelivery : null;
  if (lastVisitKey !== visitKey) {
    setLastVisitKey(() => visitKey);
    setDismissedVisitKey(() => null);
    setShowingCatalog(() => false);
    setItemPicker(() => null);
    setDelivery(() => null);
    if (productPickerOpen) dispatch(closeProductPicker());
  }

  const showCard =
    isOnSelectionLogic &&
    ruleId !== null &&
    dismissedVisitKey !== visitKey &&
    // Atlas is mid-thought, or has a question of its own on the composer.
    !streaming &&
    !disabled &&
    inputRequirement.userInput.type === "text" &&
    ruleName !== null;

  const scenarios = scenariosQuery.data?.scenarios ?? [];
  const isGenerating = scenariosQuery.data?.status === "pending";
  const hasExamples = scenarios.length > 0 || isGenerating;
  // The catalog rows are the only list a rule without example carts has.
  const showingSuggested = hasExamples && !showingCatalog;

  const submitPick = (kind: CartPickKind, items: ShopifyItemPickerItem[]) => {
    // The rule query was cleared under us; a nameless request is not sent.
    if (ruleName === null || items.length === 0) return;
    submitInput(formatCartPickReply({ ruleName, kind, items, delivery }));
  };

  const openRow = (kind: CartPickKind) => {
    if (kind === "products") {
      dispatch(openProductPicker({ type: PRODUCT_PICKER_TYPE }));
    } else {
      setItemPicker(() => kind);
    }
  };

  const tooManyProducts = selectedProducts.length > MAX_CART_PICK_PRODUCTS;

  const card = (
    <Stack
      data-testid="cart-scenario-ask"
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "12px",
        boxShadow: theme.igShadows.low,
        overflow: "hidden",
        pb: 2,
        marginX: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 5, py: 3 }}
      >
        <IgTypography variant="body1" sx={{ fontWeight: 600, minWidth: 0 }}>
          {ASK}
        </IgTypography>
        <IgIconButton
          color="primary"
          iconType="close"
          iconSize={16}
          aria-label="Dismiss cart suggestions"
          onClick={() => setDismissedVisitKey(() => visitKey)}
          sx={{ flexShrink: 0, p: 0 }}
        />
      </Stack>
      <Stack sx={{ px: 2 }}>
        {showingSuggested ? (
          <>
            {scenarios.map((scenario, index) => (
              <AskRow
                key={scenario.name}
                index={index}
                label={scenario.name}
                onClick={() => {
                  // The card only shows with a named rule; the query can
                  // still be cleared under a click in flight.
                  if (ruleName === null) return;
                  submitInput({
                    type: "text",
                    content: formatSuggestedCartRun({
                      ruleName,
                      scenarioName: scenario.name,
                      delivery,
                    }),
                  });
                }}
              />
            ))}
            {isGenerating ? (
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                sx={{ px: 5, py: 3 }}
              >
                <CircularProgress size={14} />
                <IgTypography variant="caption" color="text.secondary">
                  Generating example carts…
                </IgTypography>
              </Stack>
            ) : null}
            <SecondaryRow
              label={CATALOG_LABEL}
              onClick={() => setShowingCatalog(() => true)}
            />
          </>
        ) : (
          <>
            {GENERIC_ROWS.map((row, index) => (
              <AskRow
                key={row.kind}
                index={index}
                label={row.label}
                onClick={() => openRow(row.kind)}
              />
            ))}
            {hasExamples ? (
              <SecondaryRow
                label={BACK_LABEL}
                onClick={() => setShowingCatalog(() => false)}
              />
            ) : null}
          </>
        )}
        {offersDelivery ? (
          <DeliveryRow
            value={delivery}
            onChange={(next) => setDelivery(() => next)}
          />
        ) : null}
      </Stack>
    </Stack>
  );

  return (
    <>
      {showCard ? card : null}
      <Dialog
        disableEscapeKeyDown
        fullWidth
        maxWidth="lg"
        open={productPickerOpen}
        sx={{ zIndex: 1401 }}
        onClose={() => dispatch(closeProductPicker())}
        slotProps={{ paper: { style: { maxHeight: "700px" } } }}
      >
        <ProductPicker
          loadVariants={false}
          dialogActions={[
            ...(tooManyProducts
              ? [
                  <IgTypography key="cap" variant="caption" color="error">
                    {`Pick up to ${MAX_CART_PICK_PRODUCTS} products`}
                  </IgTypography>,
                ]
              : []),
            <IgButton
              key="cancel"
              variant="text"
              onClick={() => dispatch(closeProductPicker())}
            >
              Cancel
            </IgButton>,
            <IgButton
              key="done"
              variant="contained"
              disabled={
                selectedProducts.length === 0 ||
                tooManyProducts ||
                streaming ||
                disabled
              }
              onClick={() => {
                submitPick(
                  "products",
                  selectedProducts.map((product) => ({
                    id: product.id,
                    label: product.title,
                  }))
                );
                dispatch(closeProductPicker());
              }}
            >
              Done
            </IgButton>,
          ]}
        />
      </Dialog>
      {itemPicker ? (
        <Dialog
          fullWidth
          maxWidth="md"
          open
          sx={{ zIndex: 1401 }}
          onClose={() => setItemPicker(() => null)}
        >
          <CartPickItemPicker
            kind={itemPicker}
            saveDisabled={streaming || disabled}
            onClose={() => setItemPicker(() => null)}
            onSave={(selected) => {
              submitPick(itemPicker, selected);
              setItemPicker(() => null);
            }}
          />
        </Dialog>
      ) : null}
    </>
  );
};

const AskRow = ({
  index,
  label,
  onClick,
}: {
  index: number;
  label: ReactNode;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <ButtonBase
      disableRipple
      onClick={onClick}
      sx={{
        alignItems: "flex-start",
        borderRadius: "12px",
        display: "flex",
        gap: 3,
        justifyContent: "flex-start",
        px: 5,
        py: 2,
        textAlign: "left",
        width: "100%",
        "&:hover": {
          backgroundColor: theme.igBackgroundColors.baseLightMid,
        },
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: IgGrayPrimitives[500],
          borderRadius: "6px",
          flexShrink: 0,
          height: 20,
          width: 20,
        }}
      >
        <IgTypography
          variant="caption"
          sx={{
            color: theme.palette.text.tertiary,
            fontSize: "13px",
            lineHeight: "normal",
          }}
        >
          {index + 1}
        </IgTypography>
      </Stack>
      <IgTypography
        variant="body2"
        sx={{ flex: 1, fontSize: "12px", minWidth: 0 }}
      >
        {label}
      </IgTypography>
    </ButtonBase>
  );
};

// The delivery modifier chips under either list. Selecting one pins it to
// the next cart run; clicking it again lets it go.
const DeliveryRow = ({
  value,
  onChange,
}: {
  value: CartDeliveryModifier | null;
  onChange: (next: CartDeliveryModifier | null) => void;
}) => (
  <Stack sx={{ px: 3 }}>
    <IgDivider spacing="s" />
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      role="group"
      aria-label="Delivery"
      data-testid="cart-delivery-modifier"
      sx={{ px: 2, py: 2 }}
    >
      <IgTypography variant="caption" color="text.secondary">
        Delivery
      </IgTypography>
      {CART_DELIVERY_MODIFIERS.map((modifier) => (
        <IgChip
          key={modifier.id}
          clickable
          aria-pressed={value === modifier.id}
          // The theme's accent chip is the tinted, bordered "selected" look;
          // a filled primary chip is the palette's near-black on dark text.
          color={value === modifier.id ? "accent" : "default"}
          label={modifier.label}
          size="small"
          onClick={() => onChange(value === modifier.id ? null : modifier.id)}
        />
      ))}
    </Stack>
  </Stack>
);

// The way between the two lists — unnumbered, because it is not a cart. Sits
// on the numbered rows' grid: same inset and height, its label starting where
// their badge does, so the divider is the only thing that sets it apart.
const SecondaryRow = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <Stack sx={{ px: 3 }}>
      <IgDivider spacing="s" />
      <ButtonBase
        disableRipple
        onClick={onClick}
        sx={{
          borderRadius: "12px",
          justifyContent: "flex-start",
          px: 2,
          py: 2,
          textAlign: "left",
          width: "100%",
          "&:hover": {
            backgroundColor: theme.igBackgroundColors.baseLightMid,
          },
        }}
      >
        <IgTypography variant="body2" color="text.secondary">
          {label}
        </IgTypography>
      </ButtonBase>
    </Stack>
  );
};

// Separate component so the store-wide item list is only fetched while the
// picker dialog is mounted.
const CartPickItemPicker = ({
  kind,
  saveDisabled,
  onClose,
  onSave,
}: {
  kind: Exclude<CartPickKind, "products">;
  saveDisabled: boolean;
  onClose: () => void;
  onSave: (selected: ShopifyItemPickerItem[]) => void;
}) => {
  const config = ITEM_PICKERS[kind];
  const { items, loading } = useShopifyPickerItems(kind);

  return (
    <ShopifyItemPicker
      title={config.title}
      itemNoun={config.itemNoun}
      searchPlaceholder={config.searchPlaceholder}
      saveLabel="Done"
      items={items}
      loading={loading}
      saveDisabled={saveDisabled}
      onClose={onClose}
      onSave={onSave}
    />
  );
};
